import labels from "../../utils/labels"
import StandardButton from "../UI/buttons/StandardButton"
import LabeledSection from "../UI/LabeledSection"
import { useAppSelector } from "../../store/hooks"
import { EmployeeTransport, TransportationType } from "../../types/ganttChart"
import { useEffect, useRef, useState } from "react"
import EmployeeTransportListItem from "./EmployeeTransportListItem"
import timestampService from "../../service/timestampService"
import TransportValidationUtil from "../../utils/transportValidationUtil"

const EmployeeTransportType = () => {
  const { transportationType, employeeWorkTime } = useAppSelector((state) => state.drawerSlice.selectedEmployee!)
  const [localTransports, setLocalTransports] = useState<EmployeeTransport[]>([])
  const initialTransportRef = useRef<EmployeeTransport[]>([])

  useEffect(() => {
    setLocalTransports(transportationType)
    initialTransportRef.current = transportationType
  }, [transportationType])

  // getting all transport options
  const getTransportTitle = (type: TransportationType) => {
    switch (type) {
      case "CAR": return labels.transportCar;
      case "BIKE": return labels.transportBike;
      case "ELECTRIC_BIKE": return labels.transportElectricBike;
      case "WALK": return labels.transportWalking;
    }
  }
  const transportOptions: TransportationType[] = ["CAR", "BIKE", "ELECTRIC_BIKE", "WALK"];
  const options = transportOptions.map((type) => ({
    id: type,
    title: getTransportTitle(type),
  }))

  const hasMultipleTransports = localTransports.length > 1
  const hasChanges =
    JSON.stringify(initialTransportRef.current) !== JSON.stringify(localTransports);

  // add local transport
  const addTransport = () => {
    setLocalTransports((previousTransports) => [
      ...previousTransports,
      { type: "", timeWindow: { transportStartTime: "00:00", transportEndTime: "00:00" } }
    ])
  }

  // delete local transport
  const deleteTransport = (transportIndex: number) => {
    if (hasMultipleTransports) {
      setLocalTransports((transports) =>
        transports.filter((_, index) => index !== transportIndex)
      )
    }
  }

  // edit local transport
  const updateTransport = (
    transportIndex: number,
    updates: {
      type?: TransportationType;
      transportStartTime?: string;
      transportEndTime?: string;
    }
  ) => {
    setLocalTransports(transports =>
      transports.map((transport, index) =>
        index === transportIndex ?
          {
            type: updates.type ?? transport.type,
            timeWindow: {
              transportStartTime:
                updates.transportStartTime ?? transport.timeWindow.transportStartTime,
              transportEndTime:
                updates.transportEndTime ?? transport.timeWindow.transportEndTime,
            },
          } : transport
      )
    );
  };

  //input validation

  // empty fields
  const hasEmptyFields = localTransports.some((transport) => {
    return (
      !transport.type ||
      transport.timeWindow.transportStartTime === "00:00" ||
      transport.timeWindow.transportEndTime === "00:00"
    )
  })

  const shiftStartTimeMinutes = timestampService.timeToMinutesSum(employeeWorkTime.workTimeStart);
  const shiftEndTimeMinutes = timestampService.timeToMinutesSum(employeeWorkTime.workTimeEnd);

  const localTimeIntervals = localTransports.map((transport) => ({
    startMinutes: timestampService.timeToMinutesSum(
      transport.timeWindow.transportStartTime
    ),
    endMinutes: timestampService.timeToMinutesSum(
      transport.timeWindow.transportEndTime
    )
  }))

  // transport time is outside workhours
  const isOutsideWorkHours =
    !hasEmptyFields &&
    TransportValidationUtil.hasOutsideWorkHours(
      localTimeIntervals,
      shiftStartTimeMinutes,
      shiftEndTimeMinutes
    )

  // transport times are overlapping
  const hasOverlap =
    !hasEmptyFields && TransportValidationUtil.hasOverlap(localTimeIntervals);


  // transport time doesn't fill workhours
  const totalTransportMinutes = !hasEmptyFields
    ? TransportValidationUtil.getTotalMinutes(localTimeIntervals)
    : 0

  const worktimeMinutes = TransportValidationUtil.getWorktimeMinutes(
    shiftStartTimeMinutes,
    shiftEndTimeMinutes
  )

  const hasWorktimeGap =
    !hasEmptyFields &&
    !isOutsideWorkHours &&
    !hasOverlap &&
    totalTransportMinutes < worktimeMinutes

  let transportErrorMessage = null
  if (hasEmptyFields) {
    transportErrorMessage = labels.errorEmptyFields;
  } else if (isOutsideWorkHours) {
    transportErrorMessage = labels.errorOutsideWorkHours;
  } else if (hasOverlap) {
    transportErrorMessage = labels.errorOverlappingTimeWindow;
  } else if (hasWorktimeGap) {
    transportErrorMessage = labels.errorNotFilledWorkHours;
  } else {
    transportErrorMessage = null
  }

  // todo: save transport changes
  const handleSaveTransportChanges = () => {
    try {
      // await EmployeeSettingsService.editEmployeeTransportation(employeeShiftId, localTransports, dispatch)
    } catch {
      setLocalTransports(initialTransportRef.current)
    }
  }

  const handleDiscardTransportChanges = () => {
    setLocalTransports(initialTransportRef.current)
  }

  return (
    <LabeledSection
      title={labels.transport}
      titleSize="h4"
      spacing="small"
      extraOptions={
        <StandardButton variant="TERTIARY" onClick={addTransport} classes="!h-[17.5px]">
          {labels.addTransportType}
        </StandardButton>
      }
    >
      {/* array of transports */}
      {localTransports.map((transport, index) => (
        <EmployeeTransportListItem
          key={`${transport.type}-${index}`}
          transport={transport}
          options={options}
          onTypeChange={(newOption) => updateTransport(index, { type: newOption })}
          onStartChange={(newStart) => updateTransport(index, { transportStartTime: newStart })}
          onEndChange={(newEnd) => updateTransport(index, { transportEndTime: newEnd })}
          canDelete={hasMultipleTransports}
          onDelete={() => deleteTransport(index)}
        />
      ))}

      {/* error messages */}
      {transportErrorMessage && <p className="flex justify-end text-[var(--col-error-4)]">{transportErrorMessage}</p>}

      {/* controls for when the user edits */}
      {hasChanges &&
        <div className="flex gap-[var(--spacing-sm)] w-full justify-end">
          <StandardButton
            variant="SECONDARY"
            classes="w-fit"
            onClick={handleDiscardTransportChanges}
          >
            {labels.discard}
          </StandardButton>

          <StandardButton
            variant="PRIMARY"
            classes="w-fit"
            onClick={handleSaveTransportChanges}
            disable={!!transportErrorMessage}
          >
            {labels.save}
          </StandardButton>
        </div>
      }
    </LabeledSection>
  )
}

export default EmployeeTransportType